import React, { useState, useEffect, useRef } from 'react';
import { get, post } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const ASSET_TYPES = ['Product Photo','Product Video','Reel','Edited Photo','Raw Photo','Raw Video','Logo','Graphic','Campaign Asset','Mockup','Website Banner','Other'];

export default function BrandAssets() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name:'', asset_type:'', product_name:'', tags:'', description:'' });
  const fileRef = useRef(null);

  useEffect(()=>{ load(); },[search, filterType]);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (search) p.append('search', search);
    if (filterType) p.append('asset_type', filterType);
    get('/bc/assets?'+p.toString()).then(d=>setAssets(d.data||[])).finally(()=>setLoading(false));
  };

  const save = async () => {
    if (!fileRef.current?.files[0]) { alert('Please select a file'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', fileRef.current.files[0]);
      Object.entries(form).forEach(([k,v])=>fd.append(k,v));
      const token = localStorage.getItem('bb_token');
      await fetch('/api/bc/assets', {method:'POST',headers:{Authorization:`Bearer ${token}`},body:fd});
      setShowForm(false);
      setForm({name:'',asset_type:'',product_name:'',tags:'',description:''});
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const getFileIcon = (type) => {
    if (!type) return '📄';
    if (type.startsWith('image')) return '🖼';
    if (type.startsWith('video')) return '🎬';
    if (type.includes('pdf')) return '📕';
    return '📄';
  };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Digital Asset Library</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Central library for all brand and content assets</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={()=>setShowForm(true)}>+ Upload Asset</button>
      </div>

      <div style={{display:'flex',gap:10,marginBottom:16}}>
        <input className="form-control" placeholder="Search assets..." value={search}
          onChange={e=>setSearch(e.target.value)} style={{maxWidth:220,fontSize:12}} />
        <select className="form-control" value={filterType}
          onChange={e=>setFilterType(e.target.value)} style={{maxWidth:180,fontSize:12}}>
          <option value="">All Types</option>
          {ASSET_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? <div className="spinner"/> : assets.length === 0 ? (
        <div className="empty-state"><h3>No assets yet</h3><p>Upload your first asset</p></div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:12}}>
          {assets.map(a=>(
            <div key={a.id} style={{background:'#fff',border:'1px solid #E8E0D4',
              borderRadius:10,overflow:'hidden',cursor:'pointer'}}
              onClick={()=>window.open('/api/bc/assets/'+a.id+'/file','_blank')}>
              <div style={{height:120,background:'#F9F8F5',display:'flex',
                alignItems:'center',justifyContent:'center',fontSize:40,overflow:'hidden'}}>
                {a.file_type && a.file_type.startsWith('image/') ? (
                  <img src={'/api/bc/assets/'+a.id+'/file'} alt={a.name}
                    style={{width:'100%',height:'100%',objectFit:'cover'}} />
                ) : (
                  getFileIcon(a.file_type)
                )}
              </div>
              <div style={{padding:12}}>
                <div style={{fontSize:12,fontWeight:600,color:'#2C1F16',
                  marginBottom:4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {a.name}
                </div>
                <div style={{fontSize:10,color:'#8A8A8A',marginBottom:6}}>{a.asset_type||'Asset'}</div>
                {a.product_name && (
                  <div style={{fontSize:10,color:'#A8893D',marginBottom:6}}>{a.product_name}</div>
                )}
                {a.tags?.length > 0 && (
                  <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
                    {a.tags.slice(0,3).map(t=>(
                      <span key={t} style={{fontSize:8,background:'#F2EFE7',color:'#556B2F',
                        padding:'1px 6px',borderRadius:10}}>{t}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-hdr">
              <span className="modal-title">Upload Asset</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">File *</label>
                <input type="file" ref={fileRef} className="form-control"
                  accept="image/*,video/*,.pdf,.ai,.psd,.svg" />
              </div>
              <div className="form-group">
                <label className="form-label">Asset Name</label>
                <input className="form-control" value={form.name}
                  onChange={e=>setForm({...form,name:e.target.value})}
                  placeholder="Leave blank to use filename" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Asset Type</label>
                  <select className="form-control" value={form.asset_type}
                    onChange={e=>setForm({...form,asset_type:e.target.value})}>
                    <option value="">Select type</option>
                    {ASSET_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Product</label>
                  <input className="form-control" value={form.product_name}
                    onChange={e=>setForm({...form,product_name:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Tags (comma separated)</label>
                <input className="form-control" value={form.tags}
                  onChange={e=>setForm({...form,tags:e.target.value})}
                  placeholder="e.g. tote, product, 2026" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={2} value={form.description}
                  onChange={e=>setForm({...form,description:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Uploading...':'Upload Asset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
